import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Calendar, FileText, MessagesSquare, Settings, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { getDeviceInfo } from '@/utils/device-detection';

export function MobileBottomNav() {
  const { t } = useLanguage();
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  const deviceInfo = getDeviceInfo();

  // Add bottom padding to the body on mobile so page content isn't hidden behind the nav
  useEffect(() => {
    if (!isAuthenticated) return;
    document.body.classList.add('has-mobile-bottom-nav');
    return () => {
      document.body.classList.remove('has-mobile-bottom-nav');
    };
  }, [isAuthenticated]);

  if (!isAuthenticated) return null;

  const isStudent = user?.account_type === 'Student';
  const isParent = user?.account_type === 'Parent';
  const showSettings = !isStudent && !isParent;

  const handleSettingsClick = () => {
    if (
      localStorage.getItem('wizard-active') === 'true' &&
      localStorage.getItem('wizard-step') === '0'
    ) {
      localStorage.setItem('wizard-step', '1');
      window.dispatchEvent(new Event('wizard-state-changed'));
    }
  };

  const items: Array<{
    to: string;
    label: string;
    icon: React.ReactNode;
    id?: string;
    onClick?: () => void;
    show: boolean;
  }> = [
    {
      to: '/agenda',
      label: t('nav.agenda'),
      icon: <Calendar size={20} />,
      show: true,
    },
    {
      to: '/invoices',
      label: t('nav.invoices'),
      icon: <FileText size={20} />,
      show: true,
    },
    {
      to: '/settings',
      label: t('nav.settings'),
      icon: <Settings size={20} />,
      id: 'mobile-bottom-settings-link',
      onClick: handleSettingsClick,
      show: showSettings,
    },
    {
      to: '/my-account',
      label: t('nav.myAccount'),
      icon: <User size={20} />,
      show: true,
    },
  ];

  const visibleItems = items.filter((i) => i.show);

  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-gray-200/30 p-2 backdrop-blur-lg border-t border-border shadow-[0_-2px_10px_rgba(0,0,0,0.05)] rounded-t-3xl"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      aria-label="Mobile bottom navigation"
    > 
      <ul className={`flex items-stretch justify-around ${deviceInfo.isIOS ? 'mb-3' : 'mb-3'}`}>
        {visibleItems.map((item) => {
          const isActive =
            location.pathname === item.to ||
            (item.to !== '/' && location.pathname.startsWith(item.to));
          return (
            <li key={item.to} className="flex-1">
              <Link
                to={item.to}
                id={item.id}
                onClick={item.onClick}
                className={cn(
                  'flex flex-col items-center justify-center gap-1 py-2 px-1 transition-colors',
                  isActive
                    ? 'text-primary'
                    : 'text-foreground/60 hover:text-foreground'
                )}
              >
                <span
                  className={cn(
                    'flex items-center justify-center rounded-full transition-all',
                    isActive ? 'bg-primary/10 px-3 py-1' : 'px-3 py-1'
                  )}
                >
                  {item.icon}
                </span>
                <span className="text-[10px] font-medium leading-none truncate max-w-[64px]">
                  {item.label}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export default MobileBottomNav;
